import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { RestaurantAuthProvider } from './context/RestaurantAuthContext';
import { RestaurantDatabaseProvider } from './context/RestaurantDatabaseContext';
import ProtectedRestaurantRoute from './components/ProtectedRestaurantRoute';
import RestaurantLayout from './RestaurantLayout';

// Lazy-loaded page components for code splitting
const RestaurantLogin = lazy(() => import('./pages/RestaurantLogin'));
const RestaurantDashboard = lazy(() => import('./pages/RestaurantDashboard'));
const RestaurantBookings = lazy(() => import('./pages/RestaurantBookings'));
const RestaurantTables = lazy(() => import('./pages/RestaurantTables'));
const RestaurantCalendar = lazy(() => import('./pages/RestaurantCalendar'));
const RestaurantSettings = lazy(() => import('./pages/RestaurantSettings'));
const RestaurantCurrentBookings = lazy(() => import('./pages/RestaurantCurrentBookings'));
const RestaurantStatistics = lazy(() => import('./pages/RestaurantStatistics'));
const RestaurantDemoData = lazy(() => import('./pages/RestaurantDemoData'));

function PageLoader() {
  return (
    <div className="flex items-center justify-center py-20">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600" />
    </div>
  );
}

// Wrapper component that provides context based on URL params
function RestaurantContextWrapper({ children }: { children: React.ReactNode }) {
  const { hotelId, restaurantId } = useParams<{ hotelId: string; restaurantId: string }>();

  if (!hotelId || !restaurantId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-xl font-bold text-gray-900 mb-2">Invalid URL</h1>
          <p className="text-gray-500">Please use a valid restaurant URL</p>
        </div>
      </div>
    );
  }

  return (
    <RestaurantDatabaseProvider hotelId={hotelId} restaurantId={restaurantId}>
      <RestaurantAuthProvider hotelId={hotelId} restaurantId={restaurantId}>
        {children}
      </RestaurantAuthProvider>
    </RestaurantDatabaseProvider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Restaurant routes with dynamic hotel and restaurant IDs */}
          <Route path="/restaurant/:hotelId/:restaurantId">
            {/* Login - public */}
            <Route
              path="login"
              element={
                <RestaurantContextWrapper>
                  <RestaurantLogin />
                </RestaurantContextWrapper>
              }
            />

            {/* Protected routes */}
            <Route
              element={
                <RestaurantContextWrapper>
                  <ProtectedRestaurantRoute />
                </RestaurantContextWrapper>
              }
            >
              <Route element={<RestaurantLayout />}>
                <Route path="dashboard" element={<RestaurantDashboard />} />
                <Route path="bookings" element={<RestaurantBookings />} />
                <Route path="current" element={<RestaurantCurrentBookings />} />
                <Route path="tables" element={<RestaurantTables />} />
                <Route path="calendar" element={<RestaurantCalendar />} />
                <Route path="statistics" element={<RestaurantStatistics />} />
                <Route path="settings" element={<RestaurantSettings />} />
                <Route path="demo-data" element={<RestaurantDemoData />} />
                <Route index element={<Navigate to="dashboard" replace />} />
              </Route>
            </Route>

            {/* Default redirect to login */}
            <Route index element={<Navigate to="login" replace />} />
          </Route>

          {/* Root redirect - show instructions */}
          <Route
            path="/"
            element={
              <div className="min-h-screen flex items-center justify-center bg-[#F5F5F0] p-4">
                <div className="text-center max-w-md">
                  <h1 className="text-3xl font-bold text-[#2D3B2D] mb-4">meBooking CMS</h1>
                  <p className="text-gray-600 mb-6">
                    Restaurant booking management system
                  </p>
                  <div className="bg-white rounded-xl p-6 border border-gray-200 text-left">
                    <p className="text-sm text-gray-500 mb-2">Access your restaurant panel at:</p>
                    <code className="block bg-gray-100 p-3 rounded-lg text-sm text-gray-700 break-all">
                      /restaurant/[hotelId]/[restaurantId]/login
                    </code>
                  </div>
                  <p className="text-xs text-gray-400 mt-6">
                    Powered by meSolutions.io
                  </p>
                </div>
              </div>
            }
          />

          {/* Catch all - redirect to root */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;
